import type { GroupSettings, GroupSettingsPatch } from '#/services/settings/schema.js'
import type { AiShieldStats, ChatStats } from '#/services/stats/service.js'
import type { AiShieldActivityState } from './ai-shield-stats.js'
import type { SettingsWithRevision } from './api.js'
import { SECTIONS } from '#/services/settings/spec.js'
import { openAiShieldFlags } from './ai-shield-flags.js'
import { aiShieldActivityRow, openAiShieldActivity, paintActivityRowValue, planAiShieldStats, renderAiShieldActivity } from './ai-shield-stats.js'
import { getAiShieldStats, getSettings, getStats, messageKey, patchSettings, setLogChannel } from './api.js'
import { bootDevConsole } from './dev-console.js'
import { clear, clearError, el, errorCount, setError, setStatus } from './dom.js'
import { renderForm } from './form.js'
import { renderGroupPicker } from './group-picker.js'
import { generalIcon } from './icons.js'
import { setLang, t, WEB_LOCALES } from './labels.js'
import { planLogChannel, renderLogChannel } from './logchannel.js'
import { closeHelp, sectionHead } from './sheet.js'
import { renderStats, renderStatsError } from './stats.js'
import { applyTheme } from './theme.js'
import { applyTrust } from './trust.js'

// The orchestrator — the only module that touches window/Telegram/fetch for boot. It guards loudly at
// every step (no Telegram → no chat → no initData → load failure), then renders the form and re-renders
// from the AUTHORITATIVE settings the server returns on each save. A failed save surfaces the error AND
// reverts the UI to the last known-good settings (never leaves the screen lying). Live-verify only.

// View Transitions: animate a whole-screen re-render (the language switch re-localizes everything) as a
// cinematic cross-fade where supported, with a graceful fallback (direct DOM swap) on older WebKit and a
// hard opt-out under prefers-reduced-motion. The mutate() callback does the DOM change; startViewTransition
// snapshots + tweens.
const reduceMotionMql = typeof window.matchMedia === 'function'
  ? window.matchMedia('(prefers-reduced-motion: reduce)')
  : null

// The one spec section that renders APART from the ordinary form: AI Shield leads the page as a hero panel
// (it is the differentiator — spec order puts it first for /config too), carrying its activity readout as
// the panel's last row (v9 — it opens the activity sheet).
// Filtered by KEY (not position) so a later spec reorder can't silently put the section in twice or zero times.
const AI_SECTIONS = SECTIONS.filter(s => s.key === 'aishield')
const MAIN_SECTIONS = SECTIONS.filter(s => s.key !== 'aishield')
function withTransition(mutate: () => void): void {
  if (reduceMotionMql?.matches || typeof document.startViewTransition !== 'function') {
    mutate()
    return
  }
  document.startViewTransition(mutate)
}

// True ONLY during the brief synchronous window while flushPendingEdits() commits a focused field on the way
// out. save() reads it to enable fetch keepalive solely for that close-flush: keepalive lets the in-flight
// request survive the webview teardown, but it also caps the body at 64 KiB — and blocklist/allowlistDomains
// can serialize past that — so ordinary in-app saves must run WITHOUT it. blur()→change→save runs synchronously,
// so the flag is still true when save() reads it, and is reset before any later (normal) save can see it.
let flushingOnClose = false

// Commit a focused-but-uncommitted text field (a <textarea>, or a single-line <input> like the timezone
// free-text fallback) before the app goes away (best-effort): blurring it fires its `change` handler, which
// saves IF the value changed. The async save may not finish if the webview is torn down instantly, but
// together with the normal blur-on-tap-away this closes the common "typed, then closed while still focused"
// gap that would otherwise drop the edit silently (text fields commit on blur, not per keystroke, to avoid
// re-rendering the form mid-typing). Both element types are covered: a committed control (checkbox/select)
// fires no `change` on blur, so widening the guard to HTMLInputElement never triggers a spurious save. The
// save it triggers runs with keepalive (see above).
function flushPendingEdits(): void {
  const active = document.activeElement
  if (!(active instanceof HTMLTextAreaElement || active instanceof HTMLInputElement))
    return
  flushingOnClose = true
  try {
    active.blur() // fires the field's `change` → save() synchronously, so keepalive is read while still true
  }
  finally {
    flushingOnClose = false
  }
}

function boot(): void {
  const tg = window.Telegram?.WebApp
  if (!tg) {
    setError(t('ui-err-not-telegram'))
    return
  }
  tg.ready()
  tg.expand()
  applyTheme(tg)
  // The footer site link (static in index.html): route it through the SDK — inside Telegram, openLink
  // opens the system browser and the dashboard stays put (a plain navigation would replace the Mini App
  // INSIDE the webview); in a plain browser tab the SDK's own openLink falls back to window.open, so the
  // link works there too. The anchor's target=_blank covers the un-wired path (no SDK/openLink). Known
  // dead corner, accepted: a launch URL copied WITH its #tgWebAppVersion fragment into a plain tab makes
  // openLink post into a void transport — nobody reaches the page that way without hand-copying the
  // fragment. Wired before the later guards so the link works on error screens too.
  const site = document.querySelector<HTMLAnchorElement>('.footer a')
  if (site && typeof tg.openLink === 'function') {
    site.addEventListener('click', (e) => {
      e.preventDefault()
      tg.openLink!(site.href)
    })
  }
  tg.BackButton.show()
  // Route Back through the guide first: a tap closes an open help sheet; otherwise it closes the app.
  // One stateless dispatcher (the SDK has no offClick here) — closeHelp() reports whether it consumed it.
  tg.BackButton.onClick(() => {
    if (closeHelp())
      return
    flushPendingEdits() // commit a focused text field before leaving
    tg.close()
  })
  // The app can also close/background without the BackButton (swipe-down, OS) — flush on the way to hidden too.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden')
      flushPendingEdits()
  })

  const params = new URLSearchParams(location.search)

  // `!` (not `=== ''`): some Telegram Desktop builds leave initData UNDEFINED (not '') for inline-button
  // launches — `=== ''` would miss it and let the app fire a doomed API call (→ opaque 401) instead of
  // surfacing the real cause. Caught live 2026-06-25: desktop blank page. Fail loud with the true reason.
  // ONE guard for BOTH views (hoisted above the branch — cold-eye s76 killed the duplicated copy); for
  // the group dashboard this now precedes the no-chat guard, both truthful fatal-boot causes.
  if (!tg.initData) {
    setError(t('ui-err-no-initdata'))
    return
  }

  if (params.get('view') === 'dev') {
    const devApp = document.getElementById('app')
    if (!devApp)
      return
    // The owner console (plan: owner-console.md) — cross-group, no bound chat, so it renders in the
    // OPERATOR's client language (there is no group language to follow; unsupported → product default).
    // The load-failure fallback is the console's OWN key: ui-err-load speaks of "this group's settings",
    // wording that would misdirect on a surface with no bound group (cold-eye s76).
    setLang(tg.initDataUnsafe.user?.language_code ?? '')
    void bootDevConsole(devApp).then((res) => {
      if (!res.ok)
        setError(t(messageKey(res.err, 'dev-err-load'))) // a non-owner's 403 surfaces as exactly «forbidden»
    })
    return
  }

  // The chat id is DISPLAY/scoping only — the server re-authorizes it on every call (never trusted).
  // Prefer the launch URL ?chat, fall back to start_param / the SDK's start-param URL key.
  const candidate = params.get('chat') ?? tg.initDataUnsafe.start_param ?? params.get('tgWebAppStartParam') ?? ''
  if (!/^-?\d+$/.test(candidate) || !Number.isSafeInteger(Number(candidate))) {
    // Nessun gruppo in arrivo: NON è più un vicolo cieco. Si chiede all'utente quale vuole, ed è ciò
    // che permette di aprire la dashboard dal pulsante di menu o da un'icona sulla Home invece che
    // ripartendo ogni volta da /dashboard dentro il gruppo (sessione 84).
    //
    // La lingua del CLIENT, come per la console owner due blocchi sopra e per la stessa ragione: qui
    // non c'è ancora un gruppo, quindi non c'è una lingua di gruppo da seguire. Senza questa riga il
    // selettore cadeva sempre sul default di prodotto, e un admin con client inglese leggeva una
    // schermata italiana (rilievo N3, sessione 85).
    setLang(tg.initDataUnsafe.user?.language_code ?? '')
    const root = document.getElementById('app')
    if (root === null) {
      setError(t('ui-err-no-chat'))
      return
    }
    // `boot()` non è async e non lo diventa per questo: il selettore disegna da sé anche i propri
    // fallimenti (rete, server), quindi non c'è nulla da attendere qui. Il `catch` è l'ultima rete —
    // se perfino quello fallisse, la schermata resterebbe muta, e muta è l'unica cosa che non
    // accettiamo.
    void renderGroupPicker(root).catch(() => setError(t('ui-err-no-chat')))
    return
  }
  const chat = candidate

  const app = document.getElementById('app')
  if (!app)
    return

  // `current` = the last server-confirmed settings; the whole screen renders from it. Declared with no
  // initializer (assigned by load()); closures may reference it before then — TS allows this.
  let current: GroupSettings
  // The revision of the log destination `current` shows (logDestination, in every settings answer): sent with
  // the next choice, so a choice made from a stale screen is refused instead of landing (round 1 finding 5).
  let logRevision = 0
  // The ONE way an answer enters the screen: the settings — whose logChannelId api.ts took from the row's
  // logDestination — and that destination's revision, TOGETHER (round 2 finding 4). Assigning `current` alone
  // would let the section show one destination while the next choice carries the revision of another.
  function adopt(saved: SettingsWithRevision): void {
    current = saved.settings
    logRevision = saved.logRevision
  }
  // The stats section's state: fetched ONCE per open (moderation counts move slowly, and a refetch on
  // every save would burn rate-limit budget for nothing), then re-painted from this cache on every
  // render() — which is how a language switch re-localizes it. `statsRoot` is the section's container in
  // the CURRENT render pass; while the fetch is pending it simply stays empty (it sits at the very bottom,
  // so the late pop-in never shifts the form above).
  let statsData: ChatStats | null = null
  let statsFailed = false
  let statsRoot: HTMLElement | null = null
  // The AI-Shield activity's state. Both the row's readout AND the fetch are gated on `current.aiShield`:
  // the hero row is greyed (.off, like the section's other dependent rows) when the instrument is off, and
  // the one-shot load fires (at most once) only while it's on — so a transient fetch failure can never
  // latch a stale error that then surfaces on a later toggle-on when the feature is fine and simply has no
  // flags yet (cold-eye fold). Cached after the first success, so a later off→on→off→on doesn't refetch.
  // `aiActivityValue` is the hero row's counts span in the CURRENT render pass; `aiActivitySheetBody` the
  // open activity sheet's body (if any) — both repainted in place when the pending fetch settles.
  let aiShieldData: AiShieldStats | null = null
  let aiShieldFailed = false
  let aiShieldRequested = false
  let aiActivityValue: HTMLElement | null = null
  let aiActivitySheetBody: HTMLElement | null = null
  // The hero's tappable activity row in the CURRENT render pass — the focus-restore target when the
  // activity sheet closes, AND what a drill-down's "back" reopens the activity sheet against.
  let aiActivityRow: HTMLElement | null = null
  // The bound group's display name (from the GET response). Shown centered at the top so two dashboards
  // opened for different groups are never confused (the v3 redesign dropped the header). Set once in
  // load() and kept across saves — a settings write never changes the title.
  let groupTitle = ''
  let firstPaint = true

  // `app`/`tg` are guarded non-null above, but TS doesn't carry that narrowing into these nested closures
  // (they could run later), so the `!` assertions are required — same idiom as the bot's ctx.from!.id.
  function render(): void {
    // GROUP-language: render the whole screen in the group's configured language. `current` is the single
    // source of truth and always carries `language`, so setting it here covers load, every save, AND a live
    // language switch (the selector saves language → current updates → re-render in the new language).
    setLang(current.language)
    clear(app!)
    if (firstPaint) {
      app!.classList.add('enter') // one-shot fade-in of the panel (CSS #app.enter); reduced-motion-safe
      firstPaint = false
    }
    // 1) The sticky top bar — WHICH group this dashboard configures: a band with the name centered
    //    (v9: one static state — the scroll-linked glow is gone, user call session 70).
    //    Title-only chrome since session 69: the language selector lives in the General panel below.
    renderHeader(app!)
    // 2) Il modulo delle sezioni, SUBITO — in testa c'è ciò che si apre la dashboard per fare.
    //    ⚠️ SESSIONE 85: fino a ieri qui partiva il pannello hero di AI Shield, e le sezioni venivano
    //    dopo. L'ordine è stato rovesciato sulla misura di `audit_log` (vedi services/settings/spec.ts,
    //    v5): nei quattro gruppi vivi la modalità notturna vale 58 delle 120 modifiche reali ed era
    //    l'ULTIMA di undici, mentre AI Shield ne vale 3, è spenta ovunque, e occupava il primo schermo.
    const formRoot = el('div', { class: 'settings-form' })
    app!.append(formRoot)
    renderForm(formRoot, current, onChange, MAIN_SECTIONS)
    // 3) AI Shield — sempre il suo pannello accentato (.ai-hero), che porta ON la propria attività:
    //    l'ultima riga del .sec-group è il conteggio toccabile che apre la scheda per categoria —
    //    strumento e segnale in UNA scatola (sessione 70). Non è più in cima: sta in fondo, dov'è il
    //    suo peso d'uso. La classe resta `ai-hero` perché è il vestito del pannello, non la posizione.
    const heroRoot = el('div', { class: 'ai-hero' })
    app!.append(heroRoot)
    renderForm(heroRoot, current, onChange, AI_SECTIONS)
    // The hero's rows surface, built by renderForm just above — AI_SECTIONS is filtered by key, so exactly
    // one section renders here. The `!` is deliberate fail-fast: if a spec change ever renamed the section
    // away, appending to null throws at boot (a loud blank screen), never a silently missing feature.
    const heroGroup = heroRoot.querySelector<HTMLElement>('.sec-group')!
    const activity = aiShieldActivityRow(activityState(), openActivitySheet)
    aiActivityValue = activity.value
    aiActivityRow = activity.row
    if (!current.aiShield) {
      // Same dress as the spec rows a master toggle turns off: greyed + inert (.off), counts blanked —
      // an off instrument shows no readout, and the gated fetch below never fires for it.
      activity.row.classList.add('off')
      activity.value.textContent = ''
    }
    heroGroup.append(activity.row)
    ensureAiShieldStats()
    // 4) The bespoke log-channel control — config too, but with its own self-verifying save path
    //    (POST /api/log-channel test-posts before saving), re-rendered from `current` each pass.
    const logChannelRoot = el('div')
    app!.append(logChannelRoot)
    renderLogChannel(logChannelRoot, planLogChannel(current), { onSet: onSetLogChannel, onDisable: onDisableLogChannel })
    // 5) The General panel — the group-wide META preferences (language + silent mode), at the bottom
    //    right above the stats (user call, session 71; it sat under the hero from session 64): set-once
    //    options don't earn a slot ahead of the everyday moderation sections.
    renderGeneral(app!)
    // 6) The read-only stats section, LAST. render() rebuilds the container each pass; paintStats fills it
    //    from the cached fetch state (or leaves it empty while pending — it's at the bottom, no shift).
    statsRoot = el('div')
    app!.append(statsRoot)
    paintStats()
  }

  // Paint the stats section from its cached state. Called by render() (every repaint, incl. a language
  // switch) AND by loadStats() when the fetch settles. A failure renders the loud in-section error line —
  // scoped there on purpose: the settings form must keep working when only the stats read is down.
  function paintStats(): void {
    if (!statsRoot)
      return
    if (statsData)
      renderStats(statsRoot, statsData)
    else if (statsFailed)
      renderStatsError(statsRoot)
  }

  async function loadStats(): Promise<void> {
    try {
      statsData = await getStats(chat)
    }
    catch {
      statsFailed = true // the error state is rendered loudly in-section; the code adds no detail worth routing
    }
    paintStats()
  }

  // The cached fetch state, shaped for the view: loaded data becomes the ready model — or `error` when the
  // reply is malformed (planAiShieldStats → null: fail loud, never a half-picture); a failed load is
  // `error`; anything else is the in-flight `pending`. Recomputed per paint (the shaping is cheap).
  function activityState(): AiShieldActivityState {
    if (aiShieldData) {
      const model = planAiShieldStats(aiShieldData)
      return model ? { kind: 'ready', model } : { kind: 'error' }
    }
    return aiShieldFailed ? { kind: 'error' } : { kind: 'pending' }
  }

  // Open the activity sheet (the per-category table) — shared by the hero-row tap AND a drill-down's
  // "back" (see drill). Stores the body so a settling fetch repaints it in place (paintAiShieldActivity).
  function openActivitySheet(triggerEl?: HTMLElement): void {
    aiActivitySheetBody = openAiShieldActivity(activityState(), drill, triggerEl)
  }

  // The drill-down opener every activity count row calls (a bottom sheet of individual events). It REPLACES
  // the activity sheet (one sheet at a time), so on close we REOPEN the activity sheet — making ×, backdrop,
  // Escape and Telegram's Back step ONE level up to the category list instead of straight to the page (user
  // report: "va troppo indietro, non al menu precedente"). The reopen is deferred one frame so it runs AFTER
  // closeHelp fully unwinds, never re-entrantly (a reopen inside the close hook would mutate the sheet
  // module's `trigger`/`backdrop` while closeHelp still reads them → mis-routed focus). The t.me deep links
  // route through openTelegramLink so the Mini App stays alive while Telegram jumps to the message.
  // The transport half of the drill-down's trust control (the rule — re-read, then write — lives in
  // trust.ts). Both halves adopt the server's answer into `current` and re-render: the trusted section
  // under the sheet must never keep showing a list the server has already moved past — including on the
  // read, which is why a plain re-read repaints too. render() is safe while a sheet is open: the sheet
  // lives on document.body, outside #app, and the drill-down's own nodes are untouched by it.
  const trustIo = {
    read: async (): Promise<readonly string[]> => {
      const loaded = await getSettings(chat)
      adopt(loaded)
      render()
      return loaded.settings.trusted.userIds
    },
    write: async (userIds: string[]): Promise<void> => {
      adopt(await patchSettings(chat, { trusted: { userIds } }))
      render()
      tg.HapticFeedback?.notificationOccurred('success')
    },
  }

  function drill(category: string | null, triggerEl: HTMLElement): void {
    openAiShieldFlags({
      chat,
      category,
      triggerEl,
      trust: {
        ids: () => current.trusted.userIds,
        set: async (targetId, next) => applyTrust(trustIo, targetId, next),
      },
      onClose: () => { requestAnimationFrame(() => openActivitySheet(aiActivityRow ?? undefined)) },
      ...(typeof tg!.openTelegramLink === 'function' ? { openLink: (url: string) => tg!.openTelegramLink!(url) } : {}),
    })
  }

  // Repaint the activity views in place when the one-shot fetch settles: the hero row's counts (only while
  // the instrument is on — an off row stays blank), and — if the admin is looking at the activity sheet
  // right now — the sheet body too, so a tap made while pending doesn't strand "…" on screen forever.
  // A closed sheet's container is detached (isConnected false) and is skipped.
  function paintAiShieldActivity(): void {
    const state = activityState()
    if (aiActivityValue && current.aiShield)
      paintActivityRowValue(aiActivityValue, state)
    if (aiActivitySheetBody?.isConnected)
      renderAiShieldActivity(aiActivitySheetBody, state, drill)
  }

  // Load the AI-Shield activity ON DEMAND — only when AI Shield is (or becomes) enabled, at most ONCE.
  // This skips the fetch for the default-OFF common case AND scopes the fetch (and any failure) to when the
  // readout is live: a boot-time failure can't latch a stale "couldn't load" that would then mislead on a
  // later toggle-on. A failure shows the loud error in the activity sheet; re-opening the dashboard retries.
  function ensureAiShieldStats(): void {
    if (aiShieldRequested || !current.aiShield)
      return
    aiShieldRequested = true
    void (async () => {
      try {
        aiShieldData = await getAiShieldStats(chat)
      }
      catch {
        aiShieldFailed = true // shown loudly on the row ("—") + in the sheet; no extra detail to route
      }
      paintAiShieldActivity()
    })()
  }

  // The sticky top bar: the bound group's name as the page's TITLE (two dashboards opened for different
  // groups must never be confused). v9 (user call, session 70): ONE static state — the name centered in
  // the band, no scroll-linked glow. The v8 lit-at-top machinery (1px sentinel +
  // IntersectionObserver + the .lit class) went with it — subtraction: with the glow gone and the title
  // centered in both states, the toggle changed nothing worth its moving parts. Title-only chrome since
  // session 69 (user call): the language selector lives in the General panel with the other group-wide
  // preferences. Text via el() → a safe text node (XSS); an empty title falls back to the brand name
  // (defensive — the backend always sends a non-empty id-fallback).
  function renderHeader(root: HTMLElement): void {
    root.append(el('div', { class: 'top-bar' }, el('div', { class: 'top-title' }, groupTitle || 'TurBOT')))
  }

  // The General panel: the group-wide META preferences — the group language + silent mode — as an
  // ordinary titled panel, rendered at the BOTTOM of the page right above the stats (user call,
  // session 71): set-once options, out of the way of the everyday sections. The language selector
  // moved back here from the top bar (user call, session 69): a full-width row has room for the
  // native lang-* names the tiny top-bar dropdown couldn't fit (never translated, so someone stuck
  // in the wrong language still finds their own). Head via the shared sectionHead WITH bodyKey: the "?"
  // sits in the head band and opens the panel's guide sheet, exactly like every other section's
  // trigger (user call, session 64 — the row-level "?" beside the toggle was the odd one out).
  function renderGeneral(root: HTMLElement): void {
    root.append(sectionHead(generalIcon, 'ui-sec-general', 'ui-sec-general-body'))
    const group = el('div', { class: 'sec-group' })

    // Group language: the language TurBOT writes in for this group (and this dashboard renders in).
    // Mirrors form.ts's enum row (a <label> wrap, so tapping the text activates the select — no id
    // wiring needed); a switch re-renders the whole screen, so it saves with the cross-fade.
    const lang = el('select', {}) as HTMLSelectElement
    for (const code of WEB_LOCALES)
      lang.append(el('option', { value: code, selected: code === current.language }, t(`lang-${code}`)))
    lang.addEventListener('change', () => {
      if (lang.value !== current.language)
        void save({ language: lang.value }, true) // a language switch is a dramatic change → cross-fade
    })
    group.append(el('label', { class: 'row' }, el('span', { class: 'label' }, t('ui-language')), lang))

    // Silent mode: the bot still moderates, but doesn't post the outcome confirmations into the group.
    // The label pairs with the checkbox by for/id, so tapping the text toggles it — like every
    // spec-driven bool row.
    const silentInput = el('input', { type: 'checkbox', checked: current.silent, id: 'general-silent' }) as HTMLInputElement
    silentInput.addEventListener('change', () => {
      if (silentInput.checked !== current.silent)
        void save({ silent: silentInput.checked }, false)
    })
    group.append(el('div', { class: 'row' }, el('label', { class: 'label', for: 'general-silent' }, t('ui-silent-title')), silentInput))

    root.append(group)
  }

  function onChange(patch: GroupSettingsPatch): void {
    void save(patch, false) // a single-field edit re-renders instantly (the cross-fade is language-switch-only)
  }

  async function save(patch: GroupSettingsPatch, animate: boolean): Promise<void> {
    // A success clears the banner only if no error appeared since THIS save began — another save failing
    // meanwhile must stay on screen until it is read (judge, s105 round 2).
    const errorsBefore = errorCount()
    setStatus(t('ui-saving'), true)
    try {
      adopt(await patchSettings(chat, patch, flushingOnClose))
      if (errorCount() === errorsBefore)
        clearError()
      if (animate)
        withTransition(render)
      else
        render()
      setStatus(t('ui-saved'))
      tg!.HapticFeedback?.notificationOccurred('success')
    }
    catch (err) {
      setError(t(messageKey(err)))
      render() // revert the optimistic control back to the known-good value (no animation on error)
      setStatus('')
      tg!.HapticFeedback?.notificationOccurred('error')
    }
  }

  // The log channel saves through its OWN endpoint (setLogChannel → the self-verifying POST /api/log-channel),
  // not the settings patch path. onSet only ever fires with a client-validated id (renderLogChannel refuses
  // junk before calling us); onDisable clears it.
  function onSetLogChannel(id: string): void {
    void saveLogChannel(id)
  }
  function onDisableLogChannel(): void {
    void saveLogChannel(null)
  }

  async function saveLogChannel(value: string | null): Promise<void> {
    const errorsBefore = errorCount() // the same rule as save()
    setStatus(t('ui-saving'), true)
    try {
      adopt(await setLogChannel(chat, value, logRevision))
      if (errorCount() === errorsBefore)
        clearError()
      render()
      setStatus(t('ui-saved'))
      tg!.HapticFeedback?.notificationOccurred('success')
    }
    catch (err) {
      setError(t(messageKey(err)))
      // Deliberately do NOT re-render on failure: keep the id the admin typed in the input so they can fix the
      // channel's admin rights and retry without re-typing (the unreachable case is a fix-and-retry flow). The
      // known-good `current` is untouched (setLogChannel only assigns it on success), so nothing lies.
      setStatus('')
      tg!.HapticFeedback?.notificationOccurred('error')
    }
  }

  async function load(): Promise<void> {
    try {
      const loaded = await getSettings(chat)
      adopt(loaded)
      groupTitle = loaded.title
    }
    catch (err) {
      setError(t(messageKey(err, 'ui-err-load')))
      return
    }
    clearError()
    render()
  }

  // Settings + stats fetch together; render order is race-safe: if the stats fetch settles first, its paint
  // no-ops (no root yet) and the first render() paints it from cache; if settings settle first, loadStats()
  // paints into the live container when it arrives. The AI-Shield activity loads LAZILY instead — render()
  // calls ensureAiShieldStats(), which fetches only while AI Shield is enabled, at most once.
  void load()
  void loadStats()
}

boot()
